import toast from 'react-hot-toast';

/**
 * Toast utility with duplicate prevention
 * Prevents the same toast from appearing multiple times in quick succession
 */

// Track recent toasts to prevent duplicates
const recentToasts = new Map();
const DUPLICATE_THRESHOLD = 1000; // 1 second

/**
 * Clean up old toast entries
 */
const cleanupOldToasts = () => {
  const now = Date.now();
  for (const [key, timestamp] of recentToasts.entries()) {
    if (now - timestamp > DUPLICATE_THRESHOLD) {
      recentToasts.delete(key);
    }
  }
};

/**
 * Check if this toast was recently shown
 */
const isDuplicate = (message) => {
  cleanupOldToasts();
  const now = Date.now();
  const lastShown = recentToasts.get(message);
  
  if (lastShown && now - lastShown < DUPLICATE_THRESHOLD) {
    return true;
  }
  
  recentToasts.set(message, now);
  return false;
};

/**
 * Show success toast (with duplicate prevention)
 */
export const showSuccess = (message, options = {}) => {
  if (isDuplicate(message)) return null;
  return toast.success(message, options);
};

/**
 * Show error toast (with duplicate prevention)
 * Use this only for API errors
 */
export const showError = (message, options = {}) => {
  if (isDuplicate(message)) return null;
  return toast.error(message, options);
};

/**
 * Show API error toast
 * This is specifically for errors from API responses
 */
export const showApiError = (error, options = {}) => {
  // Extract error message from different error formats
  let message = 'An error occurred';
  
  if (typeof error === 'string') {
    message = error;
  } else if (error?.message) {
    message = error.message;
  } else if (error?.detail) {
    message = error.detail;
  } else if (error?.error) {
    message = error.error;
  }
  
  if (isDuplicate(message)) return null;
  return toast.error(message, options);
};

/**
 * Show client-side validation error (silent - no toast)
 * Use this for client-side validation errors
 */
export const showClientError = (message, options = {}) => {
  // Client-side errors are not shown in toasts
  // They should be handled by inline form validation
  console.warn('Client validation error:', message);
  return null;
};

/**
 * Show info toast (with duplicate prevention)
 */
export const showInfo = (message, options = {}) => {
  if (isDuplicate(message)) return null;
  return toast(message, options);
};

/**
 * Show loading toast (with duplicate prevention)
 */
export const showLoading = (message, options = {}) => {
  if (isDuplicate(message)) return null;
  return toast.loading(message, options);
};

/**
 * Dismiss toast
 */
export const dismissToast = (toastId) => {
  return toast.dismiss(toastId);
};

/**
 * Dismiss all toasts
 */
export const dismissAll = () => {
  return toast.dismiss();
};

// Export original toast for special cases
export { toast as originalToast };

// Default export with all methods
export default {
  success: showSuccess,
  error: showError,
  apiError: showApiError,
  clientError: showClientError,
  info: showInfo,
  loading: showLoading,
  dismiss: dismissToast,
  dismissAll: dismissAll,
};
